import React, { useState } from "react";
import { KeyRound, LogOut, Power, Trash2, Link2, Crown } from "lucide-react";
import { api, apiErr } from "../../api";
import { Badge, Button, Spinner, fmtDate } from "../../Primitives";
import { Modal } from "./UserDialog";
import { ROLE_OPTIONS, ROLE_TONE, STATUS } from "./roles";

const CONFIRM = {
  disable: ["Desativar conta", "A pessoa perde o acesso imediatamente e todas as sessões são encerradas. Os dados são preservados."],
  enable: ["Reativar conta", "A pessoa poderá entrar novamente com a senha atual."],
  revoke: ["Revogar sessões", "Todas as sessões abertas desta conta serão encerradas. Será necessário entrar novamente."],
  delete: ["Excluir conta", "A conta será removida permanentemente. O registro de atividades é mantido."],
  role: ["Alterar nível de acesso", "As sessões da pessoa serão encerradas para aplicar o novo nível."],
};

function Confirm({ kind, user, onCancel, onConfirm, busy, error }) {
  const [title, base] = CONFIRM[kind];
  const text = kind === "enable" && !user.last_login_at ? "A conta volta para convite pendente. Gere um novo link de convite para a pessoa definir a senha." : base;
  return (
    <Modal title={title} onClose={onCancel} testid="confirm-dialog">
      <p className="text-[13px] text-[var(--muted)] mb-1"><strong className="text-[var(--text)]">{user.name}</strong> · {user.email}</p>
      <p className="text-[13px] text-[var(--muted)] mb-4">{text}</p>
      {error && <p role="alert" className="text-[13px] mb-3" style={{ color: "var(--bad)" }} data-testid="confirm-error">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid="confirm-cancel">Cancelar</Button>
        <Button variant={kind === "delete" || kind === "disable" ? "danger" : "primary"} size="sm" onClick={onConfirm} disabled={busy} data-testid="confirm-ok">
          {busy ? <Spinner size={12} /> : "Confirmar"}
        </Button>
      </div>
    </Modal>
  );
}

const ICON_BTN = (testid, title, Icon, onClick, disabled) => (
  <Button variant="ghost" size="sm" title={title} aria-label={title} data-testid={testid} onClick={onClick} disabled={disabled}><Icon size={13} /></Button>
);

export default function UserRow({ u, isMe, onChanged, onInvite }) {
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const locked = isMe || u.is_primary_owner;
  const [statusLabel, statusTone] = STATUS[u.status] || [u.status, "muted"];

  const run = async () => {
    setBusy(true); setError("");
    const id = u.id;
    try {
      const k = pending.kind;
      if (k === "role") await api.patch(`/admin/users/${id}`, { role: pending.role });
      else if (k === "delete") await api.delete(`/admin/users/${id}`);
      else await api.post(`/admin/users/${id}/${k === "revoke" ? "revoke-sessions" : k}`);
      setPending(null);
      onChanged(k === "revoke" && isMe);
    } catch (e) { setError(apiErr(e)); } finally { setBusy(false); }
  };
  const regen = async () => {
    try { const { data } = await api.post(`/admin/users/${u.id}/invite`); onInvite(data, u.email); onChanged(); } catch (e) { setError(apiErr(e)); }
  };

  return (
    <tr className="mia-row" data-testid={`user-row-${u.id}`}>
      <td>
        <div className="flex items-center gap-2.5">
          <span className="w-7 h-7 rounded-full grid place-items-center text-[11px] font-semibold shrink-0 bg-[var(--accent-weak)] text-[var(--accent-ink)] border border-[var(--accent-line)]">{(u.name || u.email).slice(0, 1).toUpperCase()}</span>
          <div className="min-w-0">
            <p className="truncate flex items-center gap-1.5">{u.name || "—"}{u.is_primary_owner && <Crown size={12} className="text-[var(--accent-ink)]" aria-label="Proprietário principal" />}{isMe && <span className="ops-help">(você)</span>}</p>
            <p className="text-[12px] text-[var(--muted)] truncate">{u.email}</p>
          </div>
        </div>
      </td>
      <td>
        {locked ? <Badge tone={ROLE_TONE[u.role]} testid={`user-role-${u.id}`}>{u.role_label}</Badge> : (
          <select value={u.role} aria-label="Nível de acesso" data-testid={`user-role-select-${u.id}`} className="mia-input !h-7 !py-0 !w-auto !text-[12px]"
            onChange={(e) => setPending({ kind: "role", role: e.target.value })}>
            {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        )}
      </td>
      <td><Badge tone={statusTone} testid={`user-status-${u.id}`}>{statusLabel}</Badge>
        {u.status === "invited" && u.invite_expires_at && <p className="ops-help mt-0.5">expira {fmtDate(u.invite_expires_at)}</p>}
        {u.status === "invited" && !u.invite_expires_at && <p className="ops-help mt-0.5" style={{ color: "var(--warn)" }}>link expirado</p>}
      </td>
      <td className="mono text-[12px] text-[var(--muted)] whitespace-nowrap">{fmtDate(u.created_at)}</td>
      <td className="mono text-[12px] text-[var(--muted)] whitespace-nowrap">{u.last_login_at ? fmtDate(u.last_login_at) : "—"}</td>
      <td>
        <div className="flex justify-end gap-0.5">
          {u.status === "invited" && ICON_BTN(`user-invite-${u.id}`, "Gerar novo link de convite", Link2, regen)}
          {u.status === "active" && ICON_BTN(`user-revoke-${u.id}`, "Revogar sessões", LogOut, () => setPending({ kind: "revoke" }))}
          {!locked && (u.status === "disabled"
            ? ICON_BTN(`user-enable-${u.id}`, "Reativar conta", KeyRound, () => setPending({ kind: "enable" }))
            : ICON_BTN(`user-disable-${u.id}`, "Desativar conta", Power, () => setPending({ kind: "disable" })))}
          {!locked && ICON_BTN(`user-delete-${u.id}`, "Excluir conta", Trash2, () => setPending({ kind: "delete" }))}
        </div>
        {error && !pending && <p className="text-[11px] mt-1 text-right" style={{ color: "var(--bad)" }} role="alert">{error}</p>}
        {pending && <Confirm kind={pending.kind} user={u} busy={busy} error={error} onConfirm={run} onCancel={() => { setPending(null); setError(""); }} />}
      </td>
    </tr>
  );
}
